import React, { Component } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import './AnnouncementIndividualPage.css';

import GetAnnouncementsActions from '../../../redux/GetAnnouncementsRedux';



interface AnnouncementIndividualPageProps extends WithTranslation {
  navigate: NavigateFunction;
  announcementId: string | undefined;
  // Redux props for getting announcement from list
  announcements: any[];
  getAnnouncementsFetching: boolean;
  getAnnouncementsSuccess: boolean;
  getAnnouncements: () => void;
}

interface AnnouncementIndividualPageState {
  // No state needed for this component
}

class AnnouncementIndividualPage extends Component<AnnouncementIndividualPageProps, AnnouncementIndividualPageState> {
  constructor(props: AnnouncementIndividualPageProps) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    console.log('AnnouncementIndividualPage componentDidMount:', {
      announcementId: this.props.announcementId,
      announcements: this.props.announcements,
      announcementsLength: this.props.announcements?.length
    });
    
    // Load announcements list if not already loaded
    // Only load if we don't have announcements or if we're still fetching
    if (!this.props.announcements || this.props.announcements.length === 0) {
      console.log('Loading announcements...');
      this.props.getAnnouncements();
    } else {
      console.log('Announcements already loaded, checking for specific announcement...');
    }
  }

  componentDidUpdate(prevProps: AnnouncementIndividualPageProps) {
    // If announcements were just loaded and we still don't have the specific announcement
    if (prevProps.announcements?.length === 0 && this.props.announcements?.length > 0) {
      // Announcements were just loaded, check if we can find our announcement
      const announcement = this.props.announcements.find((item: any) => 
        String(item.id) === String(this.props.announcementId)
      );
      
      if (!announcement) {
        console.log('Announcement not found after loading:', {
          announcementId: this.props.announcementId,
          availableIds: this.props.announcements.map((item: any) => item.id)
        });
      }
    }
  }

  // Phase 5: Navigation methods implemented
  _onBackPress = () => {
    this.props.navigate('/announcements');
  };

  // Helper method to format date as "25th April 2025"
  formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'long' });
    const year = date.getFullYear();
    
    // Add ordinal suffix to day
    const getOrdinalSuffix = (day: number) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
  };

  render() {
    const { announcements, announcementId, getAnnouncementsFetching, getAnnouncementsSuccess, t } = this.props;
    
    // Debug logging
    console.log('AnnouncementIndividualPage Debug:', {
      announcementId,
      announcements,
      getAnnouncementsFetching,
      getAnnouncementsSuccess,
      announcementsLength: announcements?.length
    });
    
    // Find the announcement by ID from the announcements list
    // Handle both string and number ID types
    const announcement = announcements.find((item: any) => 
      String(item.id) === String(announcementId)
    );
    
    console.log('Found announcement:', announcement);

    return (
      <div className="announcement-individual-page">
        {/* Header matching reference project exactly */}
        <div className="page-header">
          <div className="page-header-inner">
            <div className="header-left">
              {/* Phase 5: Back button implemented */}
              <button className="header-button back-button" onClick={this._onBackPress}>
                <svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.79646 0.204319C6.07989 0.482194 6.07989 0.943465 5.80201 1.2269L2.42862 4.61141H15.2831C15.6777 4.61141 16 4.93374 16 5.33388C16 5.73402 15.6777 6.05635 15.2831 6.05635H2.42862L5.80757 9.44086C6.08545 9.72429 6.07989 10.18 5.80201 10.4634C5.51858 10.7413 5.06843 10.7413 4.78499 10.4579L0.205627 5.84517C0.144495 5.77848 0.0944773 5.70623 0.0555749 5.61731C0.0166725 5.52839 0 5.43391 0 5.33944C0 5.15048 0.0722476 4.97264 0.205627 4.8337L4.78499 0.220992C5.05731 -0.0679974 5.51303 -0.073555 5.79646 0.204319Z" fill="black"></path></svg>
                <h1 className="header-title">
                  {t ? t("announcement-individual-screen:title") : "Announcements"}
                </h1>
              </button>
            </div>
            
            <div className="header-right"></div>
          </div>
        </div>

                {/* Main content matching reference project exactly */}
        <div className="announcement-individual-container">
          {/* Loading state */}
          {getAnnouncementsFetching && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t ? t("announcement-individual-screen:loading") : "Loading announcement..."}</p>
            </div>
          )}

          {/* Success state with announcement content */}
          {getAnnouncementsSuccess && !getAnnouncementsFetching && announcement && (
            <div className="announcement-content">
              <div className="announcement-header">
                <div className="announcement-title">
                  {announcement.title}
                </div>
              </div>
              <div className="announcement-body">
                <div className="announcement-text">
                  {announcement.content}
                </div>
                <div className="announcement-date">
                  {t ? t("announcement-individual-screen:publish-date", "Published on") : "Published on"} {this.formatDate(announcement.published_at)}
                </div>
              </div>
            </div>
          )}

          {/* Empty state - when announcements loaded but specific announcement not found */}
          {getAnnouncementsSuccess && !getAnnouncementsFetching && !announcement && (
            <div className="empty-state">
              <p>{t ? t("announcement-individual-screen:empty.title") : "Announcement not found"}</p>
              <div className="debug-info">
                <p>Debug: ID: {announcementId}, Total announcements: {announcements?.length || 0}</p>
                <p>Available IDs: {announcements?.map((item: any) => item.id).join(', ') || 'None'}</p>
                <p>Last loaded: {new Date().toLocaleTimeString()}</p>
              </div>
              <div className="button-group">
                <button 
                  className="retry-button"
                  onClick={() => {
                    console.log('Retrying to load announcements...');
                    this.props.getAnnouncements();
                  }}
                >
                  {t ? t("common:refresh") : "Retry"}
                </button>
                <button 
                  className="back-button"
                  onClick={this._onBackPress}
                >
                  {t ? t("announcement-individual-screen:empty.back", "Back to Announcements") : "Back to Announcements"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

// Redux connection for getting announcements list
const mapStateToProps = (state: any) => {
  const getAnnouncements = state.getAnnouncements || {};
  
  // Extract announcements safely - handle both array and object with data property
  const announcements = getAnnouncements.announcements;
  const announcementsData = Array.isArray(announcements) ? announcements : (announcements?.data || []);
  
  // Debug logging for Redux state
  console.log('mapStateToProps Debug:', {
    stateKeys: Object.keys(state),
    getAnnouncements,
    announcements,
    announcementsData,
    announcementsDataLength: announcementsData?.length
  });
  
  return {
    announcements: announcementsData,
    getAnnouncementsFetching: getAnnouncements.fetching || false,
    getAnnouncementsSuccess: getAnnouncements.success || false,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  getAnnouncements: () => {
    console.log('Dispatching getAnnouncements action...');
    const action = GetAnnouncementsActions.getAnnouncementsRequest();
    console.log('Action to dispatch:', action);
    return dispatch(action);
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(AnnouncementIndividualPage));
